import {
  Timestamp,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { v4 as uuid } from "uuid";
import { customOrderByDESC } from "./helper";
import { buildBillPaymentUpdate } from "./bills.operations";

/* ── listener: all accounts (primary + virtual) for a community ── */
export const getAllAccountsInRealTime = (communityCode, cb) => {
  return onSnapshot(
    query(collection(db, "accounts"), where("communityCode", "==", communityCode)),
    (doc) => {
      const dataSnaps = [];
      doc.forEach((each) => {
        const temp = each.data();
        dataSnaps.push({ ...temp, temp: temp.timestamp.seconds });
      });
      cb(customOrderByDESC(dataSnaps));
    }
  );
};

/* ── listener: single account by id ── */
export const getAccount = (accountId, cb) => {
  if (accountId) {
    return onSnapshot(
      query(doc(db, "accounts", accountId)),
      (snap) => cb(snap.data())
    );
  }
};

/* ── ensure one primary account exists per community ── */
export const ensurePrimaryAccount = async (communityCode) => {
  const id = `primary_${communityCode}`;
  const ref = doc(db, "accounts", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      id,
      communityCode,
      accountRole: "primary",
      balance: 0,
      purpose: "Community Primary Account",
      timestamp: Timestamp.now(),
    });
  }
  return id;
};

/* ── create a virtual account (channel) ── */
export const saveVirtualAccount = (id, communityCode, reqPayload) => {
  const payload = {
    id,
    communityCode,
    accountRole: "virtual",
    balance: 0, // running total for this channel (informational)
    status: "active",
    ...reqPayload,
    timestamp: Timestamp.now(),
  };
  return setDoc(doc(db, "accounts", id), payload);
};

/* ── close a virtual account ── */
export const closeVirtualAccount = (accountId) => {
  return updateDoc(doc(db, "accounts", accountId), {
    status: "closed",
    closedAt: Timestamp.now(),
  });
};

/* ── ensure one cash-in-hand account exists per community ── */
export const ensureCashInHandAccount = async (communityCode) => {
  const id = `cash_${communityCode}`;
  const ref = doc(db, "accounts", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      id,
      communityCode,
      accountRole: "cash",   // distinct from "primary" and "virtual"
      balance: 0,
      purpose: "Cash-in-Hand (Treasurer)",
      timestamp: Timestamp.now(),
    });
  }
  return id;
};

/**
 * Record a cash collection — credits the cash-in-hand account (NOT the bank).
 * Atomically updates the cash account balance and marks the bill as paid.
 */
export const saveCashTransaction = async (communityCode, cashAccountId, reqPayload) => {
  const id = uuid();
  const payload = {
    id,
    communityCode,
    accountId: cashAccountId,
    ...reqPayload,
    timestamp: Timestamp.now(),
  };

  await runTransaction(db, async (transaction) => {
    const accountRef = doc(db, "accounts", cashAccountId);
    const accountSnap = await transaction.get(accountRef);
    if (!accountSnap.exists()) {
      throw new Error("Cash-in-hand account does not exist");
    }

    // Read bill doc for partial payment tracking
    let billSnap = null;
    const billRef = reqPayload.billId ? doc(db, "bills", reqPayload.billId) : null;
    if (billRef) {
      billSnap = await transaction.get(billRef);
    }

    // 1. Create transaction doc
    transaction.set(doc(db, "transactions", id), payload);

    // 2. Update cash account balance
    const currentBalance = Number(accountSnap.data().balance) || 0;
    transaction.update(accountRef, {
      balance: currentBalance + Number(reqPayload.amount),
    });

    // 3. Update bill — supports partial payments
    if (billRef && billSnap && billSnap.exists()) {
      const billData = billSnap.data();
      const paymentAmt = Number(reqPayload.billAmount ?? reqPayload.amount);
      transaction.update(billRef, buildBillPaymentUpdate(billData, id, paymentAmt, {
        paymentMode: reqPayload.paymentMode || "cash",
      }));
    }
  });
};

/* ── legacy alias – kept so callers still compile ── */
export const saveAccount = saveVirtualAccount;
